import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useToast } from '../components/Toast';
import { BoardView, TotalDevsPill } from '../components/work/BoardView';
import { CardDialog } from '../components/work/CardDialog';
import { CommitDialog } from '../components/work/CommitDialog';
import { StreamDialog } from '../components/work/StreamDialog';
import type { BoardDto, CardDto, MoveCardDto } from '../lib/types';
import { applyMove, resolveMove } from '../lib/boardMove';
import { boardQueryKey, getBoard, moveCard, updateCard, updateStream, upsertFromCard } from '../lib/workApi';

type CardDialogState = { cardId: number | null } | null;
type StreamDialogState = { streamId: number | null } | null;

const btnSecondary =
  'px-3 py-1.5 rounded border border-slate-300 text-sm text-slate-700 bg-white hover:bg-slate-100';
const btnPrimary =
  'px-3 py-1.5 rounded bg-atg-blue text-sm text-white hover:bg-atg-navy disabled:opacity-50 disabled:hover:bg-atg-blue';
const btnCommit =
  'px-3 py-1.5 rounded bg-emerald-700 text-sm text-white hover:bg-emerald-800 focus:outline-none focus-visible:ring-2 ring-emerald-700';

export function Work() {
  const canWrite = useIsAdmin();
  const qc = useQueryClient();
  const toast = useToast();
  const boardQ = useQuery({ queryKey: boardQueryKey, queryFn: getBoard });
  const board = boardQ.data;

  const [cardDialog, setCardDialog] = useState<CardDialogState>(null);
  const [streamDialog, setStreamDialog] = useState<StreamDialogState>(null);
  const [commitOpen, setCommitOpen] = useState(false);
  const [activeCard, setActiveCard] = useState<CardDto | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 5 } });
  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
    keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space'] },
  });
  const sensors = useSensors(pointerSensor, keyboardSensor);

  const move = useMutation({
    mutationFn: ({ card, dto }: { card: CardDto; dto: MoveCardDto }) => moveCard(card.id, dto),
    onMutate: async ({ card, dto }) => {
      await qc.cancelQueries({ queryKey: boardQueryKey });
      const previous = qc.getQueryData<BoardDto>(boardQueryKey);
      if (previous) qc.setQueryData<BoardDto>(boardQueryKey, applyMove(previous, card.id, dto));
      return { previous };
    },
    onError: (_err, { card }, context) => {
      if (context?.previous) qc.setQueryData(boardQueryKey, context.previous);
      toast.error(`Couldn't move "${card.title}". It's back where it was.`);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: boardQueryKey }),
  });

  // Header −/+ buttons: nudge a stream's total headcount (M) by one, optimistically.
  // `usedWorkers` is server-derived and is never touched optimistically.
  const adjustWorkers = useMutation({
    mutationFn: ({
      streamId, workerCount, name,
    }: { streamId: number; workerCount: number; name: string }) =>
      updateStream(streamId, { name, workerCount }),
    onMutate: async ({ streamId, workerCount }) => {
      await qc.cancelQueries({ queryKey: boardQueryKey });
      const previous = qc.getQueryData<BoardDto>(boardQueryKey);
      if (previous) {
        qc.setQueryData<BoardDto>(boardQueryKey, {
          ...previous,
          streams: previous.streams.map((s) =>
            s.id === streamId ? { ...s, workerCount } : s,
          ),
        });
      }
      return { previous };
    },
    onError: (_err, { name }, context) => {
      if (context?.previous) qc.setQueryData(boardQueryKey, context.previous);
      toast.error(`Couldn't update the dev count for "${name}".`);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: boardQueryKey }),
  });

  // Tile month pill: re-send the card with only targetMonth changed, optimistically.
  const setMonth = useMutation({
    mutationFn: ({ card, month }: { card: CardDto; month: number | null }) =>
      updateCard(card.id, { ...upsertFromCard(card), targetMonth: month }),
    onMutate: async ({ card, month }) => {
      await qc.cancelQueries({ queryKey: boardQueryKey });
      const previous = qc.getQueryData<BoardDto>(boardQueryKey);
      if (previous) {
        qc.setQueryData<BoardDto>(boardQueryKey, {
          ...previous,
          streams: previous.streams.map((s) => ({
            ...s,
            cards: s.cards.map((c) => (c.id === card.id ? { ...c, targetMonth: month } : c)),
          })),
        });
      }
      return { previous };
    },
    onError: (_err, { card }, context) => {
      if (context?.previous) qc.setQueryData(boardQueryKey, context.previous);
      toast.error(`Couldn't set the month for "${card.title}".`);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: boardQueryKey }),
  });

  function onSetMonth(cardId: number, month: number | null) {
    const card = findCard(cardId);
    if (card && card.targetMonth !== month) setMonth.mutate({ card, month });
  }

  function nudgeWorkers(streamId: number, delta: 1 | -1) {
    const stream = board?.streams.find((s) => s.id === streamId);
    if (!stream) return;
    const workerCount = Math.max(0, stream.workerCount + delta);
    if (workerCount === stream.workerCount) return;
    adjustWorkers.mutate({ streamId, workerCount, name: stream.name });
  }

  function rememberOpener() {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }
  function restoreFocus() {
    const el = openerRef.current;
    openerRef.current = null;
    if (el) window.setTimeout(() => el.isConnected && el.focus(), 0);
  }

  const openCard = (cardId: number | null) => {
    rememberOpener();
    setCardDialog({ cardId });
  };
  const openStream = (streamId: number | null) => {
    rememberOpener();
    setStreamDialog({ streamId });
  };
  const closeCard = () => {
    setCardDialog(null);
    restoreFocus();
  };
  const closeStream = () => {
    setStreamDialog(null);
    restoreFocus();
  };
  const openCommit = () => {
    rememberOpener();
    setCommitOpen(true);
  };
  const closeCommit = () => {
    setCommitOpen(false);
    restoreFocus();
  };

  function findCard(id: number): CardDto | undefined {
    return board?.streams.flatMap((s) => s.cards).find((c) => c.id === id);
  }

  function onDragStart(e: DragStartEvent) {
    setActiveCard(findCard(Number(e.active.id)) ?? null);
  }

  function onDragEnd(e: DragEndEvent) {
    setActiveCard(null);
    if (!board) return;
    const card = findCard(Number(e.active.id));
    // Cross-section drops on a card go after it when the dragged card's centre is below the over card's.
    const dragged = e.active.rect.current.translated;
    const overRect = e.over?.rect;
    const below =
      !!dragged && !!overRect && dragged.top + dragged.height / 2 > overRect.top + overRect.height / 2;
    const dto = card ? resolveMove(board, card.id, e.over?.id, below) : null;
    if (card && dto) move.mutate({ card, dto });
  }

  if (boardQ.isLoading) {
    return <div className="w-[95%] mx-auto text-slate-500 text-sm">Loading…</div>;
  }
  if (boardQ.isError || !board) {
    return (
      <div className="w-[95%] mx-auto text-red-700 text-sm" role="alert">
        Couldn't load the work board. Refresh the page to try again.
      </div>
    );
  }

  const hasStreams = board.streams.length > 0;
  const editingStream =
    streamDialog?.streamId != null ? board.streams.find((s) => s.id === streamDialog.streamId) ?? null : null;
  return (
    <div className="w-[95%] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-atg-navy">Work</h1>
          {hasStreams && <TotalDevsPill board={board} />}
        </div>
        <div className="flex items-center gap-2">
          {!canWrite && (
            <span className="text-xs text-slate-500 border border-slate-300 rounded-full px-2">View only</span>
          )}
          {canWrite && (
            <button type="button" id="commit-board" onClick={openCommit} className={btnCommit}>
              Commit
            </button>
          )}
          <Link to="/print" target="_blank" rel="noopener" className={btnSecondary}>
            Export PDF
          </Link>
          {canWrite && (
            <>
              <button type="button" onClick={() => openStream(null)} className={btnSecondary}>
                + New Stream
              </button>
              <button type="button" onClick={() => openCard(null)} disabled={!hasStreams} className={btnPrimary}>
                + New Card
              </button>
            </>
          )}
        </div>
      </div>

      {hasStreams ? (
        <BoardView
          board={board}
          canWrite={canWrite}
          sensors={canWrite ? sensors : undefined}
          onOpenCard={openCard}
          onEditStream={openStream}
          onAdjustWorkers={nudgeWorkers}
          onSetMonth={canWrite ? onSetMonth : undefined}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={() => setActiveCard(null)}
          activeCard={activeCard}
        />
      ) : (
        <div className="border border-dashed border-slate-300 rounded-lg py-16 text-center text-slate-600">
          <p className="font-medium text-slate-800">No work streams yet.</p>
          {canWrite ? (
            <>
              <p className="mt-1 text-sm">Add a stream to start planning.</p>
              <button type="button" onClick={() => openStream(null)} className={`${btnPrimary} mt-4`}>
                + New Stream
              </button>
            </>
          ) : (
            <p className="mt-1 text-sm">Nothing has been planned yet.</p>
          )}
        </div>
      )}

      {cardDialog && (
        <CardDialog
          key={cardDialog.cardId ?? 'new'}
          board={board}
          cardId={cardDialog.cardId}
          canWrite={canWrite}
          onClose={closeCard}
          onShowCard={(id) => setCardDialog({ cardId: id })}
        />
      )}
      {streamDialog && (
        <StreamDialog
          key={streamDialog.streamId ?? 'new'}
          streamId={streamDialog.streamId}
          stream={editingStream}
          onClose={closeStream}
        />
      )}
      {commitOpen && <CommitDialog onClose={closeCommit} />}
    </div>
  );
}
